import React from 'react';
import cn from 'classnames';
import { utcToZonedTime } from 'date-fns-tz';

import { Icons } from '@icons/icons';

import { formatDate } from '@utils/kpi-utils/date';
import { getTaskStatusIconWithTooltip } from '@components/common/Gantt/TaskList/utils';

import { Task } from '@interfaces/ganttTasks/ganttTasks';

import { EGanttTaskType } from '@enums/ganttTasks';

import { formatTemplateDefault, timezoneUTC } from '@constants/common';

import styles from './TaskListTable.module.scss';

const TaskListTableDefault: React.FC<{
	rowHeight: number;
	tasks: Task[];
	onExpanderClick: (task: Task) => void;
}> = ({
	rowHeight,
	tasks,
	onExpanderClick,
}) => {
	const milestones = tasks.filter(task => task.type === EGanttTaskType.milestone);

	return (
		<div className={styles.taskListWrapper}>
			{tasks.map(task => {
				const isTask = task.type === EGanttTaskType.task;
				const isMilestone = task.type === EGanttTaskType.milestone;

				const milestoneIndex = milestones.findIndex(milestone => milestone.id === task.id);
				const isEvenMilestone = milestoneIndex % 2 === 1;

				const expanderIcon = task.type === EGanttTaskType.milestone
					? task.hideChildren === true ? Icons.chevronDown() : Icons.chevronUp()
					: undefined;

				const assignees = task.assignees?.map(({ fullName }: { fullName: string }) => fullName).join(', ');

				return (
					<div
						key={`${task.id}row`}
						className={cn(styles.taskListTableRowWrapper, {
							[styles.milestoneRowHighlight]: isEvenMilestone,
							[styles.taskRow]: isTask,
						})}
						style={{ minHeight: rowHeight }}
					>
						<div className={styles.taskListTableRow}>
							<div
								className={styles.taskListCell}
								style={{
									minWidth: '20px',
									maxWidth: '20px',
									paddingRight: '30px',
								}}
							>
								{isMilestone
									&& <div
										className={styles.taskListIcon}
										onClick={() => onExpanderClick(task)}
									>
										{expanderIcon}
									</div>
								}
								{isTask
									&& <div className={styles.taskListIcon}>
										{getTaskStatusIconWithTooltip(task.status?.name)}
									</div>
								}
							</div>
							<div
								className={styles.taskListCell}
								style={{
									minWidth: '150px',
									maxWidth: '150px',
									paddingRight: '30px',
								}}
							>
								{task.name}
							</div>
							<div
								className={styles.taskListCell}
								style={{
									minWidth: '200px',
									maxWidth: '200px',
									paddingRight: '30px',
								}}
							>
								{assignees ? assignees : '-'}
							</div>
							<div
								className={styles.taskListCell}
								style={{
									minWidth: '120px',
									maxWidth: '120px',
									paddingRight: '30px',
								}}
							>
								{task.isWithoutStartDate
									? '-'
									: formatDate(utcToZonedTime(task.start, timezoneUTC), formatTemplateDefault)
								}
							</div>
							<div
								className={styles.taskListCell}
								style={{
									minWidth: '120px',
									maxWidth: '120px',
								}}
							>
								{task.isWithoutEndDate
									? '-'
									: formatDate(utcToZonedTime(task.end, timezoneUTC), formatTemplateDefault)
								}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default TaskListTableDefault;
